import { useState, useEffect, useRef, useCallback } from 'react';
import { MediaItem } from '../types';

interface CachedMedia {
  item: MediaItem;
  loaded: boolean;
  loading: boolean;
  error: boolean;
  element?: HTMLImageElement | HTMLVideoElement;
}

interface MediaCacheState {
  [key: string]: CachedMedia;
}

export const useMediaCache = (media: MediaItem[]) => {
  const [cache, setCache] = useState<MediaCacheState>({});
  const [preloadedItems, setPreloadedItems] = useState<Set<string>>(new Set());
  const loadingQueue = useRef<Set<string>>(new Set());
  const preloadQueue = useRef<Set<string>>(new Set());

  // Initialize cache for all media items
  useEffect(() => {
    const newCache: MediaCacheState = {};
    media.forEach(item => {
      if (!cache[item.id]) {
        newCache[item.id] = {
          item,
          loaded: false,
          loading: false,
          error: false
        };
      }
    });
    
    if (Object.keys(newCache).length > 0) {
      setCache(prev => ({ ...prev, ...newCache }));
    }
  }, [media, cache]);

  // Preload media item
  const preloadMedia = useCallback(async (item: MediaItem): Promise<boolean> => {
    if (preloadedItems.has(item.id) || loadingQueue.current.has(item.id)) {
      return preloadedItems.has(item.id);
    }

    loadingQueue.current.add(item.id);
    
    setCache(prev => ({
      ...prev,
      [item.id]: {
        ...prev[item.id],
        loading: true,
        error: false
      }
    }));

    try {
      if (item.type === 'image') {
        return await preloadImage(item);
      } else {
        return await preloadVideo(item);
      }
    } catch (error) {
      console.warn(`Failed to preload ${item.type}:`, item.url, error);
      setCache(prev => ({
        ...prev,
        [item.id]: {
          ...prev[item.id],
          loading: false,
          error: true
        }
      }));
      return false;
    } finally {
      loadingQueue.current.delete(item.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadedItems]);

  // Preload image
  const preloadImage = useCallback((item: MediaItem): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      
      img.onload = () => {
        setCache(prev => ({
          ...prev,
          [item.id]: {
            ...prev[item.id],
            loaded: true,
            loading: false,
            element: img
          }
        }));
        setPreloadedItems(prev => new Set(prev).add(item.id));
        resolve(true);
      };
      
      img.onerror = () => {
        setCache(prev => ({
          ...prev,
          [item.id]: {
            ...prev[item.id],
            loading: false,
            error: true
          }
        }));
        resolve(false);
      };
      
      img.crossOrigin = 'anonymous';
      img.src = item.url;
    });
  }, []);

  // Preload video
  const preloadVideo = useCallback((item: MediaItem): Promise<boolean> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      
      video.oncanplaythrough = () => {
        setCache(prev => ({
          ...prev,
          [item.id]: {
            ...prev[item.id],
            loaded: true,
            loading: false,
            element: video
          }
        }));
        setPreloadedItems(prev => new Set(prev).add(item.id));
        resolve(true);
      };
      
      video.onerror = () => {
        setCache(prev => ({
          ...prev,
          [item.id]: {
            ...prev[item.id],
            loading: false,
            error: true
          }
        }));
        resolve(false);
      };
      
      video.preload = 'auto';
      video.crossOrigin = 'anonymous';
      video.src = item.url;
      video.load();
    });
  }, []);

  // Aggressive preloading - preload current, next, and previous items
  const preloadAdjacentMedia = useCallback((currentIndex: number) => {
    const indicesToPreload = [
      currentIndex, // Current
      (currentIndex + 1) % media.length, // Next
      (currentIndex - 1 + media.length) % media.length, // Previous
      (currentIndex + 2) % media.length, // Next + 1
      (currentIndex - 2 + media.length) % media.length, // Previous - 1
    ];

    indicesToPreload.forEach(index => {
      const item = media[index];
      if (item && !preloadedItems.has(item.id) && !preloadQueue.current.has(item.id)) {
        preloadQueue.current.add(item.id);
        preloadMedia(item);
      }
    });
  }, [media, preloadedItems, preloadMedia]);

  // Get cached media state
  const getCachedMedia = useCallback((itemId: string) => {
    return cache[itemId];
  }, [cache]);

  // Check if media is loaded
  const isMediaLoaded = useCallback((itemId: string) => {
    return preloadedItems.has(itemId);
  }, [preloadedItems]);

  // Check if media is loading
  const isMediaLoading = useCallback((itemId: string) => {
    return cache[itemId]?.loading || false;
  }, [cache]);

  // Get loading progress
  const getLoadingProgress = useCallback(() => {
    const total = media.length;
    const loaded = preloadedItems.size;
    return total > 0 ? (loaded / total) * 100 : 0;
  }, [media.length, preloadedItems.size]);

  // Preload all media (for initial load)
  const preloadAllMedia = useCallback(async () => {
    console.log('Starting aggressive preloading of all media...');
    
    // Preload first 3 items immediately
    const initialItems = media.slice(0, 3);
    await Promise.all(initialItems.map(item => preloadMedia(item)));
    
    // Then preload the rest in batches
    const remainingItems = media.slice(3);
    const batchSize = 2;
    
    for (let i = 0; i < remainingItems.length; i += batchSize) {
      const batch = remainingItems.slice(i, i + batchSize);
      await Promise.all(batch.map(item => preloadMedia(item)));
    }
    
    console.log('All media preloaded successfully');
  }, [media, preloadMedia]);

  return {
    cache,
    preloadedItems,
    preloadMedia,
    preloadAdjacentMedia,
    getCachedMedia,
    isMediaLoaded,
    isMediaLoading,
    getLoadingProgress,
    preloadAllMedia
  };
};
